import { ReactElement, useCallback, useMemo, useState } from 'react'
import { Alert, Box, IconButton, Tooltip, Typography } from '@mui/material'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import { useTranslation } from 'react-i18next'
import ConfirmationDialog from '../../Common/ConfirmationDialog'
import { inviteUser } from '../../../services/userServices'
import { MAX_USERS_PER_BATCH } from '../constants'
import BulkSendWarning from './BulkSendWarning'

interface BulkResendActionProps {
    orgId: string
    accountName?: string
    /** Selected users still awaiting sign-in. The parent filters out anyone who can't be invited. */
    selectedUserIds: string[]
    /**
     * Send to every user in the account who hasn't signed in, rather than the ticked rows — the server
     * resolves the population so hundreds of ids never cross the wire.
     */
    /** Refresh the grid: the Status column is what shows the invitations landing. */
    onChanged: () => void
    /** Clear the row selection once a batch has been accepted. */
    onCompleted: () => void
    /**
     * UBQ-5486: a batch went to the background worker — follow it and refresh when it finishes. Called
     * instead of `onChanged`, never as well as it.
     */
    onBatchStarted: (batchId: string, startedAt: number) => void
    /** True while a batch is still sending, so a second one cannot be started on top of it. */
    batchRunning?: boolean
}

/**
 * UBQ-5212: the per-row Resend Invitation button, promoted to act on a selection.
 *
 * <p>UBQ-5486: a resend always goes to the background worker, which paces the sends. Refreshing the grid
 * on the response therefore showed the listing as it was before any invitation had gone out. The batch is
 * now followed to completion by the parent and the grid is refreshed once, at the end.
 */
const BulkResendAction = ({
    orgId,
    accountName,
    selectedUserIds,
    onChanged,
    onCompleted,
    onBatchStarted,
    batchRunning = false,
}: BulkResendActionProps): ReactElement => {
    const { t } = useTranslation()
    const [confirmOpen, setConfirmOpen] = useState(false)
    const [sending, setSending] = useState(false)

    const count = selectedUserIds.length
    const overLimit = count > MAX_USERS_PER_BATCH
    const disabled = count === 0 || sending || batchRunning
    // The server rejects anything past MAX_USERS_PER_BATCH (UserServiceImpl), so over-limit is
    // genuinely unsendable, not just discouraged.
    const isDisabled = disabled || overLimit

    const send = useCallback(async () => {
        setConfirmOpen(false)
        setSending(true)
        try {
            // UBQ-5212: same endpoint as an invite — it takes the selected users and re-sends to the
            // ones still waiting. inviteUser surfaces the API's own message, success or failure.
            const response = await inviteUser(
                { resendUserIds: selectedUserIds },
                orgId,
            )
            // Release the ticks either way: they are keyed by row index and stop meaning anything once
            // the grid refreshes.
            onCompleted()
            if (response?.batchId && response.startedAt) {
                /* UBQ-5486: hand the batch to the parent and let it refresh when the last invitation has
                   gone out. Refreshing here is what showed one user of thirty. */
                onBatchStarted(response.batchId, response.startedAt)
            } else {
                // No batch to follow (an error, or a response from before this field existed).
                onChanged()
            }
        } finally {
            setSending(false)
        }
    }, [orgId, selectedUserIds, onChanged, onCompleted, onBatchStarted])

    const confirmBody = useMemo(
        () => (
            <Box component="div">
                <Typography component="div" variant="body1">
                    {t('bulkResendConfirmSelected', {
                        count,
                        account: accountName ?? '',
                    })}
                </Typography>
                {/* What pressing Send actually does — which account they are invited to, and that
                    people who already signed in are left alone. Both are easy to get wrong when acting
                    on a selection rather than one named person. */}
                <Alert severity="info" sx={{ mt: 1, py: 0 }}>
                    {t('bulkResendInfo', { account: accountName ?? '' })}
                </Alert>
                {/* Unconditional, per the ticket's acceptance criteria — the previous
                    threshold-gated one-liner meant a send under 200 carried no warning at all.
                    Supersedes that one-liner; the count line self-emphasises past the threshold. */}
                <Box component="div" sx={{ mt: 2 }}>
                    <BulkSendWarning
                        count={count}
                        accountName={accountName}
                        showContinuePrompt
                    />
                </Box>
            </Box>
        ),
        [count, accountName, t],
    )

    const tooltip = (): string => {
        // UBQ-5486: say why the button is dead rather than leaving it faded with no explanation.
        if (batchRunning) return t('bulkInviteBatchRunning')
        if (count === 0) return t('bulkResendSelectFirst')
        if (overLimit) {
            return t('bulkResendOverLimit', {
                count,
                max: MAX_USERS_PER_BATCH,
            })
        }
        return t('bulkResendTooltip', { count })
    }

    return (
        <>
            {/* The Box keeps the tooltip working while the button is disabled — MUI does not fire
                pointer events on a disabled button, so Tooltip needs a live wrapper to listen on. */}
            <Tooltip title={tooltip()}>
                <Box
                    component="span"
                    sx={{
                        cursor: isDisabled ? 'not-allowed' : 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        marginRight: '10px',
                    }}
                >
                    <IconButton
                        id="bulk-resend-invitation-button"
                        color="primary"
                        /* A real button rather than a click handler on the SVG:
                           focusable, operable by keyboard, and it reports its disabled
                           state to assistive tech instead of only looking faded. */
                        disabled={isDisabled}
                        aria-disabled={isDisabled}
                        aria-label={t('bulkResendTitle')}
                        onClick={() => setConfirmOpen(true)}
                        sx={{
                            padding: '2px',
                            opacity: isDisabled ? 0.4 : 1,
                            '&.Mui-disabled': { color: 'primary.main' },
                        }}
                    >
                        <ForwardToInboxIcon />
                    </IconButton>
                </Box>
            </Tooltip>
            {confirmOpen && (
                <ConfirmationDialog
                    openDialog={confirmOpen}
                    DialogHeaderText={t('bulkResendTitle')}
                    DialogBodyText={confirmBody}
                    confirmtext={t('bulkResendConfirmAction')}
                    confirmId="bulk-resend-confirm-button"
                    cancelId="bulk-resend-cancel-button"
                    handleComfirm={send}
                    handleClose={() => setConfirmOpen(false)}
                />
            )}
        </>
    )
}

export default BulkResendAction
